import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import { getIsTrainIncident } from '@rsos/base-utils';
import { isTestAlert } from '@rsos/components/capstone/DataCards/utils';
import InfoWindowContent from '@rsos/components/capstone/common/Map/InfoWindow/InfoWindowContent';
import AlertActiveAssailantSelected from '@rsos/components/capstone/common/Map/assets/alert_active_assailant_selected.svg';
import AlertActiveAssailantSelectedHover from '@rsos/components/capstone/common/Map/assets/alert_active_assailant_selected_hover.svg';
import AlertSelected from '@rsos/components/capstone/common/Map/assets/alert_selected.svg';
import AlertSelectedHover from '@rsos/components/capstone/common/Map/assets/alert_selected_hover.svg';
import AlertTestSelected from '@rsos/components/capstone/common/Map/assets/alert_test_selected.svg';
import AlertTestSelectedHover from '@rsos/components/capstone/common/Map/assets/alert_test_selected_hover.svg';
import AlertTrainSelected from '@rsos/components/capstone/common/Map/assets/alert_train_selected.svg';
import AlertTrainSelectedHover from '@rsos/components/capstone/common/Map/assets/alert_train_selected_hover.svg';
import AllAlert from '@rsos/components/capstone/common/Map/assets/allalerts.svg';
import AllAlertActiveAssailant from '@rsos/components/capstone/common/Map/assets/allalerts_active_assailant.svg';
import AllAlertActiveAssailantHover from '@rsos/components/capstone/common/Map/assets/allalerts_active_assailant_hover.svg';
import AllAlertHover from '@rsos/components/capstone/common/Map/assets/allalerts_hover.svg';
import AllAlertTest from '@rsos/components/capstone/common/Map/assets/allalerts_test.svg';
import AllAlertTestHover from '@rsos/components/capstone/common/Map/assets/allalerts_test_hover.svg';
import AllAlertTrain from '@rsos/components/capstone/common/Map/assets/allalerts_train.svg';
import AllAlertTrainHover from '@rsos/components/capstone/common/Map/assets/allalerts_train_hover.svg';
import AllAlertUnread from '@rsos/components/capstone/common/Map/assets/allalerts_unread.svg';
import AllAlertUnreadHover from '@rsos/components/capstone/common/Map/assets/allalerts_unread_hover.svg';
import AllCallMarker from '@rsos/components/capstone/common/Map/assets/allcall_marker.svg';
import AllCallMarkerHover from '@rsos/components/capstone/common/Map/assets/allcall_marker_hover.svg';
import AllCallSMS from '@rsos/components/capstone/common/Map/assets/allcall_textto911.svg';
import AllCallSMSHover from '@rsos/components/capstone/common/Map/assets/allcall_textto911_hover.svg';
import CallLandline from '@rsos/components/capstone/common/Map/assets/call_landline.svg';
import CallSelected from '@rsos/components/capstone/common/Map/assets/call_selected.svg';
import CallSelectedHover from '@rsos/components/capstone/common/Map/assets/call_selected_hover.svg';
import CallVOIP from '@rsos/components/capstone/common/Map/assets/call_voip.svg';
import CallStatusAnswered from '@rsos/components/capstone/common/Map/assets/calls/call_status_answered.svg';
import CallStatusAnsweredHover from '@rsos/components/capstone/common/Map/assets/calls/call_status_answered_hover.svg';
import CallStatusOnHold from '@rsos/components/capstone/common/Map/assets/calls/call_status_on_hold.svg';
import CallStatusOnHoldHover from '@rsos/components/capstone/common/Map/assets/calls/call_status_on_hold_hover.svg';
import CallStatusReleased from '@rsos/components/capstone/common/Map/assets/calls/call_status_released.svg';
import CallStatusReleasedHover from '@rsos/components/capstone/common/Map/assets/calls/call_status_released_hover.svg';
import CallStatusRinging from '@rsos/components/capstone/common/Map/assets/calls/call_status_ringing.svg';
import CallStatusRingingHover from '@rsos/components/capstone/common/Map/assets/calls/call_status_ringing_hover.svg';
import HistoricAlertMarker from '@rsos/components/capstone/common/Map/assets/historic_alert_marker.svg';
import HistoricAlertMarkerHover from '@rsos/components/capstone/common/Map/assets/historic_alert_marker_hover.svg';
import HistoricCallMarker from '@rsos/components/capstone/common/Map/assets/historic_call_marker.svg';
import HistoricCallMarkerHover from '@rsos/components/capstone/common/Map/assets/historic_call_marker_hover.svg';
import SMSSelected from '@rsos/components/capstone/common/Map/assets/sms_selected.svg';
import W3W from '@rsos/components/capstone/common/Map/assets/w3w.svg';
import W3W_HOVER from '@rsos/components/capstone/common/Map/assets/w3w_hover.svg';
import {
  HISTORIC_LOC_ZINDEX,
  MOST_RECENT_LOC_ZINDEX,
  W3W_ZINDEX as W3WZINDEX,
} from '@rsos/components/capstone/common/Map/constants/mapZIndexes';
import {
  ACTIVE_MARKER_OPACITY,
  INACTIVE_MARKER_OPACITY,
} from '@rsos/components/capstone/common/Map/constants/markerOpacity';
import { centerMapOnJursidiction } from '@rsos/components/capstone/common/Map/useDeckGL/utils';
import {
  createGoogleMarker,
  createGoogleMarkerCircle,
  createGoogleInfoWindow,
  clearInstanceListeners,
  renderIntoInfoWindow,
  renderIntoExistingInfoWindow,
  getIsUnacceptedActiveAssailant,
  getIsUnacceptedTrainIncident,
} from '@rsos/components/capstone/common/Map/utils';
import { getIconsUrl } from '@rsos/components/capstone/common/Map/utils/getIconsUrl';
import { ACCEPTED } from '@rsos/constants/alertStatuses';
import { ACTIVE_ASSAILANT } from '@rsos/constants/alertTypes';
import {
  BE_CALL_ALERTING,
  BE_CALL_ANSWERED,
  BE_HOLD_CALL,
  BE_END_CALL,
  BE_RETRIEVE_CALL,
} from '@rsos/constants/callStatuses';
import { ALERTS, EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { SOURCE_SMS } from '@rsos/constants/locationSources';
import {
  LANDLINE,
  VOIP,
  TEXT,
  OTHER,
  PHASE,
} from '@rsos/constants/locationTypes';
import { DEFAULT_ZOOM_LEVEL } from '@rsos/maps/constants';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import { useCallQueueFilters } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { workstationPositionSelector } from '@rsos/reduxDataStore/src/features/selectors/workstationSelectors';
import getMostRecentCallStatus from '@rsos/utils/getMostRecentCallStatus';
import {
  getMostRecentLoc,
  getMostRecentLocations,
} from '@rsos/utils/getMostRecentLoc';
import { isRegularAlert } from '@rsos/utils/isRegularAlert';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import usePrevious from '@rsos/utils/usePrevious';
import AlertPopup from '../../JurisdictionView/Map/markers/alerts/AlertPopup';
import { isIgnoreType } from '../../JurisdictionView/Map/markers/calls/ignoreUtils';
import { getMarkerOpacity, shouldMarkerDim } from '../utils/callFiltering';

const revertMarkerIcon = (marker, hasCallStatus) => {
  const markerIcon = marker.getIcon();

  // alerts
  if (markerIcon === AllAlertUnreadHover) {
    marker.setIcon(AllAlertUnread);
  } else if (markerIcon === AllAlertHover) {
    marker.setIcon(AllAlert);
  } else if (markerIcon === AllAlertActiveAssailantHover) {
    marker.setIcon(AllAlertActiveAssailant);
  } else if (markerIcon === AllAlertTrainHover) {
    marker.setIcon(AllAlertTrain);
  } else if (markerIcon === AllAlertTestHover) {
    marker.setIcon(AllAlertTest);
  }
  // sms & calls
  else if (markerIcon === AllCallSMSHover) {
    marker.setIcon(AllCallSMS);
  } else if (markerIcon === AllCallMarkerHover) {
    marker.setIcon(AllCallMarker);
  } else if (hasCallStatus && markerIcon === CallStatusAnsweredHover) {
    marker.setIcon(CallStatusAnswered);
  } else if (hasCallStatus && markerIcon === CallStatusOnHoldHover) {
    marker.setIcon(CallStatusOnHold);
  } else if (hasCallStatus && markerIcon === CallStatusReleasedHover) {
    marker.setIcon(CallStatusReleased);
  } else if (hasCallStatus && markerIcon === CallStatusRingingHover) {
    marker.setIcon(CallStatusRinging);
  }
};

const setCallStatusMarkerIcon = ({
  marker,
  filteredCalls,
  hoveredID,
  hasCallStatus,
}) => {
  const callStatus = getMostRecentCallStatus({
    callerID: marker.rsosID,
    callQueue: filteredCalls,
    hasAniAliAccess: hasCallStatus,
    hasCallStatusAccess: hasCallStatus,
  });

  if (hoveredID) {
    if (hasCallStatus && callStatus) {
      switch (callStatus) {
        case BE_CALL_ANSWERED:
        case BE_RETRIEVE_CALL:
          marker.setIcon(CallStatusAnsweredHover);
          break;
        case BE_HOLD_CALL:
          marker.setIcon(CallStatusOnHoldHover);
          break;
        case BE_END_CALL:
          marker.setIcon(CallStatusReleasedHover);
          break;
        case BE_CALL_ALERTING:
          marker.setIcon(CallStatusRingingHover);
          break;
      }
    } else {
      marker.setIcon(AllCallMarkerHover);
    }
  } else {
    if (hasCallStatus && callStatus) {
      switch (callStatus) {
        case BE_CALL_ANSWERED:
        case BE_RETRIEVE_CALL:
          marker.setIcon(CallStatusAnswered);
          break;
        case BE_HOLD_CALL:
          marker.setIcon(CallStatusOnHold);
          break;
        case BE_END_CALL:
          marker.setIcon(CallStatusReleased);
          break;
        case BE_CALL_ALERTING:
          marker.setIcon(CallStatusRinging);
          break;
      }
    } else {
      marker.setIcon(AllCallMarker);
    }
  }
};

const setAniAliMarkerIcon = (marker, incident, isHovered) => {
  const classOfServiceCategory = incident?.class_of_service_category;
  if (classOfServiceCategory === TEXT) {
    marker.setIcon(SMSSelected);
  } else if (classOfServiceCategory === VOIP) {
    marker.setIcon(CallVOIP);
  } else if (
    classOfServiceCategory === LANDLINE ||
    classOfServiceCategory === OTHER
  ) {
    marker.setIcon(CallLandline);
  } else {
    if (isHovered) {
      marker.setIcon(CallSelectedHover);
    } else {
      marker.setIcon(CallSelected);
    }
  }
};

const initialMapState = {
  [EMERGENCY_CALLS]: [],
  [ALERTS]: [],
};

/**
 * Loop through currentIncidents and each incident's locations to render
 * markers.
 * @param {String} activeIncidentID - ID of incident being viewed
 * @param {String} activeQueueTab - ALERTS or EMERGENCY_CALLS
 * @param {Array.<Object>} currentIncidents - List of locations (alerts or
 * calls) to draw on Map (Note:name not final)
 * @param {Array.<Object>} currentIncidentsData - List of additional data for
 * alerts pertaining to the locations of the same index
 * @param {String} hoveredID - ID of alert or call being hovered on the map or
 * in the queue
 * @param {String} IDWithNewAlert - most recent incoming Alert ID
 * @param {String} incidentType - ALERTS or EMERGENCY_CALLS
 * @param {Boolean} isAllView - Flag for if user is viewing all alerts/calls or
 * an individual alert/call
 * @param {Object} map - Google Maps instance
 * @param {String} numberWithNewLocation - Caller ID with most recent location
 * @param {Function} onMarkerAdd - Callback function when a new marker is drawn
 * @param {Function} onMarkerClick - onClick handler markers
 * @param {Function} onMarkerHover - Hover effect handler for markers
 * @param {Function} onRemoveID - function to set state in JV for removed alert
 * @param {Function} onMarkerAdd - callback function for when a marker has
 * been drawn on the map
 * @param {String} removedID - ID of removed Alert or Call
 * @param {array.<Object>} unreadAlerts - list of unread alerts
 * @param {Object} userProfile - profile of current user

 * VIEW_TWENTY_FIVE for showing X number of historic locations
 */
const Markers = ({
  activeIncidentID = null,
  activeQueueTab,
  boundingBox,
  inactiveIncidentID,
  currentIncidents = {
    [EMERGENCY_CALLS]: [],
    [ALERTS]: [],
  },
  currentIncidentsData = [],
  hoveredID,
  IDWithNewAlert = null,
  incidentType = EMERGENCY_CALLS,
  isAllView = true,
  map,
  numberWithNewLocation = null,
  onMarkerAdd,
  onMarkerClick = () => {},
  onMarkerHover = () => {},
  onRemoveID = () => {},
  removedID = null,
  unreadAlerts,
  userProfile = {},
  hasW3w,
  w3wCoordinates,
  declinedAlertID,
  filteredCalls,
  hasCallStatus,
  isMapIconDisabled,
  handleObliqueImageryMarkerClicked,
  hasEagleViewAccess,
}) => {
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [infoWindows, setInfoWindows] = useState(initialMapState);
  const [markers, setMarkers] = useState(initialMapState);
  const [w3wMarker, setw3wMarker] = useState();
  const [markerCircles, setMarkerCircles] = useState(initialMapState);
  const [previousIncidents, setPreviousIncidents] = useState(initialMapState);
  const [previousIncidentsData, setPreviousIncidentsData] = useState([]);
  const [prevInactiveIncidentID, setPrevInactiveIncidentID] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(null);
  // Call taker mode filtering
  const { callFilters, hasActiveCallFilters } = useCallQueueFilters();
  const workStation = useSelector(workstationPositionSelector);

  const handlew3wMarkers = () => {
    let displayw3wLat,
      displayw3wLng,
      w3wPosition,
      w3wResultMarker,
      w3wInfoWindow,
      w3wInfoWContent,
      infoWindowOpts;

    if (hasW3w) {
      if (w3wCoordinates.lat && w3wCoordinates.lng) {
        displayw3wLat = parseFloat(w3wCoordinates.lat).toFixed(5);
        displayw3wLng = parseFloat(w3wCoordinates.lng).toFixed(5);
        w3wPosition = new window.google.maps.LatLng(
          displayw3wLat,
          displayw3wLng,
        );
      }
      w3wInfoWContent = `<div id="w3wContent"><div>w3w</div><div>${displayw3wLat},${displayw3wLng}</div><div>`;
      infoWindowOpts = {
        content: w3wInfoWContent,
        incidentType: '',
        headerDisabled: true,
        disableAutoPan: true,
      };
      w3wInfoWindow = createGoogleInfoWindow(infoWindowOpts);
      if (w3wMarker) {
        w3wMarker.setPosition(w3wPosition);
        if (w3wPosition) {
          panMapTo({
            mapProvider: GOOGLE,
            mapInstance: map,
            latitude: w3wPosition.lat(),
            longitude: w3wPosition.lng(),
            zoom: null,
          });
        }
        clearInstanceListeners(w3wMarker);

        if (!isMapIconDisabled) {
          w3wMarker.addListener('mouseover', () => {
            w3wMarker.setIcon(W3W_HOVER);
            w3wInfoWindow.open(map, w3wMarker);
          });
          w3wMarker.addListener('mouseout', () => {
            w3wMarker.setIcon(W3W);
            w3wInfoWindow.close(map, w3wMarker);
          });
          w3wMarker.setCursor('pointer');
        } else {
          w3wMarker.setCursor('crosshair');
          w3wMarker.addListener('click', handleObliqueImageryMarkerClicked);
        }
      } else {
        w3wResultMarker = createGoogleMarker({
          position: w3wPosition,
          map: map,
          icon: W3W,
          zIndex: W3WZINDEX,
        });

        if (w3wResultMarker && map && w3wPosition) {
          w3wResultMarker.setMap(map);
          panMapTo({
            mapProvider: GOOGLE,
            mapInstance: map,
            latitude: w3wPosition.lat(),
            longitude: w3wPosition.lng(),
            zoom: null,
          });
          if (!isMapIconDisabled) {
            w3wResultMarker.addListener('mouseover', () => {
              w3wResultMarker.setIcon(W3W_HOVER);
              w3wInfoWindow.open(map, w3wResultMarker);
            });
            w3wResultMarker.addListener('mouseout', () => {
              w3wResultMarker.setIcon(W3W);
              w3wInfoWindow.close(map, w3wResultMarker);
            });
            w3wResultMarker.setCursor('pointer');
          } else {
            w3wResultMarker.setCursor('crosshair');
            w3wResultMarker.addListener(
              'click',
              handleObliqueImageryMarkerClicked,
            );
          }
          setw3wMarker(w3wResultMarker);
        }
      }
    } else {
      if (w3wMarker) {
        setw3wMarker(null);
        w3wMarker.setMap(null);
        clearInstanceListeners(w3wMarker);
        clearInstanceListeners(w3wResultMarker);
        if (!isAllView && incidentType[0].position) {
          panMapTo({
            mapProvider: GOOGLE,
            mapInstance: map,
            latitude: incidentType[0].position.lat(),
            longitude: incidentType[0].position.lng(),
            zoom: null,
          });
        }
      }
      if (w3wInfoWindow) {
        w3wInfoWindow.setMap(null);
        clearInstanceListeners(w3wInfoWindow);
      }
    }
  };

  const createMarker = ({
    currentViewType,
    map,
    incident,
    incidentIdx,
    incidentType,
    isAllView,
    mostRecentIncidentIdx,
  }) => {
    const hasLocation = isValidCoordinates(incident);

    const isUnacceptedActiveAssailant =
      incidentType === ALERTS &&
      getIsUnacceptedActiveAssailant(
        currentIncidentsData[isAllView ? incidentIdx : 0],
      );
    const isUnacceptedTrainIncident =
      incidentType === ALERTS &&
      getIsUnacceptedTrainIncident(
        currentIncidentsData[isAllView ? incidentIdx : 0],
      );
    const isTestAlert =
      incidentType === ALERTS &&
      currentIncidentsData[
        isAllView ? incidentIdx : 0
      ]?.emergency_type?.name?.startsWith('TEST');

    const iconUrl = getIconsUrl({
      incident,
      incidentIdx,
      incidentType,
      isAllView,
      mostRecentIncidentIdx,
      isUnacceptedActiveAssailant,
      isUnacceptedTrainIncident,
      filteredCalls,
      hasCallStatus,
      isTestAlert,
    });
    const compareAgainstRecentIdx =
      incidentType === ALERTS
        ? incidentIdx > mostRecentIncidentIdx
        : incidentIdx < mostRecentIncidentIdx;

    // From Google's docs:
    // Renders the Marker on the specified map or panorama. If map is set to null, the marker will be removed.
    const shouldShowMarker =
      hasLocation &&
      !isIgnoreType(incident) &&
      (isAllView ||
        (mostRecentIncidentIdx !== undefined &&
          mostRecentIncidentIdx === incidentIdx) ||
        (!isAllView && compareAgainstRecentIdx));

    const lat = parseFloat(incident?.latitude);
    const lng = parseFloat(incident?.longitude);

    const markerOpts = {
      map: shouldShowMarker ? map : null,
      position: {
        lat: isNaN(lat) ? 0 : lat,
        lng: isNaN(lng) ? 0 : lng,
      },
      ...(iconUrl
        ? {
            icon: {
              url: iconUrl,
            },
          }
        : {}),
      isUnacceptedActiveAssailant,
      isUnacceptedTrainIncident,
      opacity: getMarkerOpacity(
        incidentType,
        incident?.caller_id,
        currentViewType === incidentType
          ? ACTIVE_MARKER_OPACITY
          : incidentType === EMERGENCY_CALLS && currentViewType === ALERTS
            ? INACTIVE_MARKER_OPACITY
            : isUnacceptedActiveAssailant || isUnacceptedTrainIncident
              ? ACTIVE_MARKER_OPACITY
              : INACTIVE_MARKER_OPACITY,
        {
          activeQueueTab,
          shouldMarkerDim,
          hasActiveCallFilters,
          callQueue: filteredCalls,
          callFilters,
          workStation,
        },
      ),
      incidentType,
      isTT911: incident?.isTT911,
      rsosID:
        incidentType === ALERTS
          ? currentIncidentsData[isAllView ? incidentIdx : 0].alert_id
          : incident?.caller_id,
      zIndex:
        incidentType === ALERTS // TODO: update alert after reversing alert queue
          ? incidentIdx === 0
            ? MOST_RECENT_LOC_ZINDEX
            : HISTORIC_LOC_ZINDEX
          : incidentIdx === mostRecentIncidentIdx // only updates for calls
            ? MOST_RECENT_LOC_ZINDEX
            : HISTORIC_LOC_ZINDEX, // lower z-index for historic locations
      incident,
      incidentIdx,
      mostRecentIncidentIdx,
    };
    return createGoogleMarker(markerOpts);
  };

  const createMarkerCircle = ({
    map,
    incident,
    incidentIdx,
    incidentType,
    mostRecentIncidentIdx,
  }) => {
    const isHistoricLoc =
      (incidentType === ALERTS && incidentIdx > 0) ||
      (incidentType === EMERGENCY_CALLS &&
        mostRecentIncidentIdx !== undefined &&
        incidentIdx < mostRecentIncidentIdx);

    const shouldShowCircle =
      !isIgnoreType(incident) &&
      (isAllView ||
        (incidentType === ALERTS && incidentIdx === 0) ||
        (incidentType === EMERGENCY_CALLS &&
          mostRecentIncidentIdx !== undefined &&
          incidentIdx === mostRecentIncidentIdx) ||
        (!isAllView &&
          ((incidentType === EMERGENCY_CALLS &&
            mostRecentIncidentIdx !== undefined &&
            incidentIdx < mostRecentIncidentIdx) ||
            (incidentType === ALERTS && incidentIdx > 0))));

    const uncertaintyRadius = incident?.uncertainty_radius;

    const lat = parseFloat(incident?.latitude);
    const lng = parseFloat(incident?.longitude);

    const markerCircleOpts = {
      strokeColor: isHistoricLoc
        ? 'transparent'
        : incidentType === ALERTS
          ? '#5C6E9D'
          : '#06c08b',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: isHistoricLoc
        ? 'transparent'
        : incidentType === ALERTS
          ? '#5C6E9D'
          : '#06c08b',
      fillOpacity: 0.35,
      map: shouldShowCircle ? map : null,
      center: {
        lat: isNaN(lat) ? 0 : lat,
        lng: isNaN(lng) ? 0 : lng,
      },
      radius:
        !uncertaintyRadius || uncertaintyRadius < 0
          ? 0
          : parseFloat(uncertaintyRadius),
      incidentType,
      clickable: false,
      rsosID:
        incidentType === ALERTS
          ? currentIncidentsData[0].alert_id
          : incident?.caller_id,
    };
    const markerCircle = createGoogleMarkerCircle(markerCircleOpts);
    return markerCircle;
  };

  const handleMarkerAdded = incidentType => {
    onMarkerAdd(incidentType);
  };

  const handleMarkerReady = (
    marker,
    map,
    incident,
    incidentIdx,
    isAllView,
    incidentType,
    mostRecentIncidentIdx,
  ) => {
    if (!isMapIconDisabled) {
      const isUnacceptedActiveAssailant = getIsUnacceptedActiveAssailant(
        currentIncidentsData[isAllView ? incidentIdx : 0],
      );
      const isUnacceptedTrainIncident = getIsUnacceptedTrainIncident(
        currentIncidentsData[isAllView ? incidentIdx : 0],
      );
      const events = {
        onClick: () =>
          handleMarkerClick(incident, incidentIdx, incidentType, isAllView),
        onMouseEnter: () =>
          handleMouseEnter(
            marker,
            incident,
            incidentIdx,
            incidentType,
            mostRecentIncidentIdx,
            isUnacceptedActiveAssailant,
            isUnacceptedTrainIncident,
          ),
        onMouseLeave: () =>
          handleMouseLeave(
            marker,
            incident,
            incidentIdx,
            incidentType,
            mostRecentIncidentIdx,
            isUnacceptedActiveAssailant,
            isUnacceptedTrainIncident,
          ),
      };
      /**
       * Map React click and hover events to Google Maps event listeners
       */
      const eventMapping = {
        onClick: 'click',
        onMouseEnter: 'mouseover',
        onMouseLeave: 'mouseout',
      };
      Object.keys(events).forEach(eventName => {
        marker.addListener(eventMapping[eventName], () => {
          events[eventName](marker);
        });
      });
    }

    // Info Windows handling
    if (incidentType === EMERGENCY_CALLS) {
      const caller_id = incident?.caller_id;
      const hasLocation = !!incident?.longitude && !!incident?.latitude;
      const infoWindowOpts = {
        content: `<div id="emergencycall-${caller_id}" class="default-infoWindow"/>`,
        incidentType,
        rsosID: caller_id,
        headerDisabled: true,
        disableAutoPan: true,
        shouldShowWindow: hasLocation,
      };
      const infoWindow = createGoogleInfoWindow(infoWindowOpts);

      if (hasLocation) {
        const callStatus =
          hasCallStatus &&
          filteredCalls[caller_id]?.callStatuses &&
          filteredCalls[caller_id].callStatuses[
            filteredCalls[caller_id].callStatuses.length - 1
          ];
        const callContent = (
          <InfoWindowContent
            type={EMERGENCY_CALLS}
            data={incident}
            callStatus={callStatus}
          />
        );

        renderIntoInfoWindow(
          `emergencycall-${caller_id}`,
          infoWindow,
          callContent,
        );
        if (!isMapIconDisabled) {
          marker.addListener('mouseover', () => {
            infoWindow.open(map, marker);
          });
          marker.addListener('mouseout', () => {
            infoWindow.close(map, marker);
          });
          marker.setCursor('pointer');
        } else {
          marker.setCursor('crosshair');
        }
      }
      return infoWindow;
    } else {
      if (incidentType === ALERTS) {
        const alertData = isAllView
          ? currentIncidentsData[incidentIdx]
          : currentIncidentsData[0];
        const infoWindowOpts = {
          content: `<div id="${alertData.alert_id}" class="default-infoWindow"/>`,
          incidentType,
          rsosID: alertData.alert_id,
          headerDisabled: true,
          disableAutoPan: true,
        };
        const infoWindow = createGoogleInfoWindow(infoWindowOpts);
        const alertContent = (
          <AlertPopup
            data={alertData}
            userProfile={userProfile}
            isAllView={isAllView}
            createdAt={alertData.location_history[incidentIdx]?.created_at}
          />
        );
        renderIntoInfoWindow(alertData.alert_id, infoWindow, alertContent);
        if (!isMapIconDisabled) {
          marker.addListener('mouseover', event => {
            infoWindow.setPosition(event.latLng);
            infoWindow.open(map, marker);
          });
          marker.addListener('mouseout', () => {
            infoWindow.close(map, marker);
          });
          marker.setCursor('pointer');
        } else {
          marker.setCursor('crosshair');
        }
        return infoWindow;
      }
    }
  };

  /**
   * Function to handle clicking on a marker
   * @param {Object} incident - Alert or call location object
   * @param {Number} incidentIdx - index of `incident` in `currentIncidents`
   * @param {String} incidentType - ALERT or EMERGENCY_CALLS
   * @param {Boolean} isAllView - Flag for if user is viewing all alerts/calls or not
   */
  function handleMarkerClick(incident, incidentIdx, incidentType, isAllView) {
    if (incidentType === ALERTS) {
      const alertID = currentIncidentsData[incidentIdx].alert_id;
      onMarkerClick(alertID, incidentType, isAllView);
    } else {
      const callerID = incident?.caller_id;
      onMarkerClick(callerID, incidentType, isAllView);
    }
  }

  /**
   * Function to handle hovering over a marker
   * @param {Object} marker - Google Maps marker instance
   * @param {Object} incident - Alert or call location object
   * @param {Number} incidentIdx - index of `incident` in `currentIncidents`
   * @param {String} incidentType - ALERT or EMERGENCY_CALLS
   */
  function handleMouseEnter(
    marker,
    incident,
    incidentIdx,
    incidentType,
    mostRecentIncidentIdx,
    isUnacceptedActiveAssailant,
    isUnacceptedTrainIncident,
  ) {
    if (isAllView) {
      const callerID = incident?.caller_id;
      if (incidentType === EMERGENCY_CALLS) {
        if (
          incident?.source === SOURCE_SMS ||
          marker.isTT911 ||
          incident?.class_of_service_category === TEXT
        ) {
          marker.setIcon(AllCallSMSHover);
        } else {
          setCallStatusMarkerIcon({
            marker,
            filteredCalls,
            hoveredID: callerID,
            hasCallStatus,
          });
        }
        onMarkerHover(callerID, 'map', EMERGENCY_CALLS);
      } else {
        const alertID = currentIncidentsData[incidentIdx].alert_id;
        const isTestAlert =
          currentIncidentsData[incidentIdx]?.emergency_type?.name?.startsWith(
            'TEST',
          );
        onMarkerHover(alertID, 'map', ALERTS);
        marker.isUnread
          ? marker.setIcon(AllAlertUnreadHover)
          : marker.setIcon(
              isUnacceptedActiveAssailant
                ? AllAlertActiveAssailantHover
                : isUnacceptedTrainIncident
                  ? AllAlertTrainHover
                  : isTestAlert
                    ? AllAlertTestHover
                    : AllAlertHover,
            );
      }
    } else {
      if (incidentType === EMERGENCY_CALLS) {
        if (incidentIdx < mostRecentIncidentIdx) {
          marker.setIcon(HistoricCallMarkerHover);
        } else {
          if (
            incident?.source === SOURCE_SMS ||
            incident?.class_of_service_category === TEXT
          ) {
            marker.setIcon(SMSSelected);
          } else {
            setAniAliMarkerIcon(marker, incident, true);
          }
        }
      } else {
        if (incidentIdx > 0) {
          marker.setIcon(HistoricAlertMarkerHover);
        } else {
          if (isUnacceptedActiveAssailant) {
            marker.setIcon(AlertActiveAssailantSelectedHover);
          } else if (isUnacceptedTrainIncident) {
            marker.setIcon(AlertTrainSelectedHover);
          } else if (isTestAlert) {
            marker.setIcon(AlertTestSelectedHover);
          } else {
            marker.setIcon(AlertSelectedHover);
          }
        }
      }
    }
  }

  /**
   * Function to handle hovering over a marker
   * @param {Object} marker - Google Maps marker instance
   * @param {Object} incident - Alert or call location object
   * @param {Number} incidentIdx - index of `incident` in `currentIncidents`
   * @param {String} incidentType - ALERT or EMERGENCY_CALLS
   */
  function handleMouseLeave(
    marker,
    incident,
    incidentIdx,
    incidentType,
    mostRecentIncidentIdx,
    isUnacceptedActiveAssailant,
    isUnacceptedTrainIncident,
  ) {
    if (isAllView) {
      if (incidentType === EMERGENCY_CALLS) {
        if (
          incident?.source === SOURCE_SMS ||
          marker.isTT911 ||
          incident?.class_of_service_category === TEXT
        ) {
          marker.setIcon(AllCallSMS);
        } else {
          setCallStatusMarkerIcon({ marker, filteredCalls, hasCallStatus });
        }
      } else {
        const isTestAlert =
          currentIncidentsData[incidentIdx]?.emergency_type?.name?.startsWith(
            'TEST',
          );

        marker.isUnread
          ? marker.setIcon(AllAlertUnread)
          : marker.setIcon(
              isUnacceptedActiveAssailant
                ? AllAlertActiveAssailant
                : isUnacceptedTrainIncident
                  ? AllAlertTrain
                  : isTestAlert
                    ? AllAlertTest
                    : AllAlert,
            );
      }
    } else {
      if (incidentType === EMERGENCY_CALLS) {
        if (incidentIdx !== mostRecentIncidentIdx) {
          marker.setIcon(HistoricCallMarker);
        } else {
          if (
            incident?.source === SOURCE_SMS ||
            incident?.class_of_service_category === TEXT
          ) {
            marker.setIcon(SMSSelected);
          } else {
            setAniAliMarkerIcon(marker, incident);
          }
        }
      } else {
        if (incidentIdx > 0) {
          marker.setIcon(HistoricAlertMarker);
        } else {
          marker.setIcon(
            isUnacceptedActiveAssailant
              ? AlertActiveAssailantSelected
              : isUnacceptedTrainIncident
                ? AlertTrainSelected
                : isTestAlert
                  ? AlertTestSelected
                  : AlertSelected,
          );
        }
      }
    }
    onMarkerHover(
      null,
      'map',
      incidentType === ALERTS ? ALERTS : EMERGENCY_CALLS,
    );
  }

  const clearMarker = marker => {
    if (marker) {
      marker.setMap(null);
      clearInstanceListeners(marker);
    }
  };

  const clearInfoWindow = infoWindow => {
    if (infoWindow) {
      infoWindow.setMap(null);
      clearInstanceListeners(infoWindow);
    }
  };

  /**
   * Clears infoWindows of a given marker type, otherwise clear all
   * @param {?String} [markerType=null] - `EMERGENCY_CALLS` or `ALERTS`
   */
  const clearInfoWindows = (markerType = null) => {
    if (infoWindows[EMERGENCY_CALLS].length || infoWindows[ALERTS].length) {
      if (markerType) {
        infoWindows[markerType].forEach(infoWindow => {
          if (infoWindow) {
            infoWindow.setMap(null);
            clearInstanceListeners(infoWindow);
          }
        });
        setInfoWindows({
          ...infoWindows,
          [markerType]: [],
        });
      } else {
        Object.keys(infoWindows).forEach(incidentType => {
          infoWindows[incidentType].forEach(infoWindow => {
            if (infoWindow) {
              infoWindow.setMap(null);
              clearInstanceListeners(infoWindow);
            }
          });
        });
        setInfoWindows(initialMapState);
      }
    }
  };

  /**
   * Clears markers of a given marker type, otherwise clear all
   * @param {?String} [markerType=null] - `EMERGENCY_CALLS` or `ALERTS`
   */
  const clearMarkers = (markerType = null) => {
    if (markers[EMERGENCY_CALLS].length || markers[ALERTS].length) {
      if (markerType) {
        markers[markerType].forEach(marker => {
          marker.setMap(null);
          clearInstanceListeners(marker);
        });
        setMarkers({
          ...markers,
          [markerType]: [],
        });
      } else {
        Object.keys(markers).forEach(incidentType => {
          markers[incidentType].forEach(marker => {
            if (marker) {
              marker.setMap(null);
              clearInstanceListeners(marker);
            }
          });
        });
        setMarkers(initialMapState);
      }
    }
  };

  /**
   * Clears marker circles of a given marker type, otherwise clear all
   * @param {?String} [markerType=null] - `EMERGENCY_CALLS` or `ALERTS`
   */
  const clearMarkerCircles = (markerType = null) => {
    if (markerCircles[EMERGENCY_CALLS].length || markerCircles[ALERTS].length) {
      if (markerType) {
        markerCircles[markerType].forEach(circle => {
          circle.setMap(null);
          clearInstanceListeners(circle);
        });
        setMarkerCircles({
          ...markerCircles,
          [markerType]: [],
        });
      } else {
        Object.keys(markerCircles).forEach(incidentType => {
          markerCircles[incidentType].forEach(circle => {
            if (circle) {
              circle.setMap(null);
              clearInstanceListeners(circle);
            }
          });
        });
        setMarkerCircles(initialMapState);
      }
    }
  };

  // Draw all markers/circles for when switching views/activeIncidentID
  const drawInitialMarkers = () => {
    if (map) {
      // Create map markers for both alerts and calls
      const mostRecentCallsIdx = currentIncidents[EMERGENCY_CALLS].length - 1;
      const mapMarkersCalls =
        isAllView || (activeIncidentID && incidentType === EMERGENCY_CALLS)
          ? currentIncidents[EMERGENCY_CALLS].map((incident, incidentIdx) =>
              createMarker({
                currentViewType: activeQueueTab,
                map,
                incident,
                incidentIdx,
                incidentType: EMERGENCY_CALLS,
                isAllView,
                mostRecentIncidentIdx: mostRecentCallsIdx,
              }),
            )
          : [];

      const mapMarkersAlerts =
        isAllView || (activeIncidentID && incidentType === ALERTS)
          ? currentIncidents[ALERTS].map((incident, incidentIdx) =>
              createMarker({
                currentViewType: activeQueueTab,
                map,
                incident,
                incidentIdx,
                incidentType: ALERTS,
                isAllView,
                mostRecentIncidentIdx: 0,
              }),
            )
          : [];

      const mapInfoWindowsCalls = mapMarkersCalls.map((marker, markerIdx) =>
        handleMarkerReady(
          marker,
          map,
          currentIncidents[EMERGENCY_CALLS][markerIdx],
          markerIdx,
          isAllView,
          EMERGENCY_CALLS,
          mostRecentCallsIdx,
        ),
      );

      // TODO: update after reversing alerts queue
      const mapInfoWindowsAlerts = mapMarkersAlerts.map((marker, markerIdx) =>
        handleMarkerReady(
          marker,
          map,
          currentIncidents[ALERTS][markerIdx],
          markerIdx,
          isAllView,
          ALERTS,
        ),
      );

      // Circles are only drawn in the detail view
      const mapMarkerCirclesCalls =
        !isAllView && activeIncidentID && incidentType === EMERGENCY_CALLS
          ? currentIncidents[EMERGENCY_CALLS].map((incident, incidentIdx) =>
              createMarkerCircle({
                map,
                incident,
                incidentIdx,
                incidentType: EMERGENCY_CALLS,
                mostRecentIncidentIdx: mostRecentCallsIdx,
              }),
            )
          : [];
      // TODO: update after reversing alerts queue

      const mapMarkerCirclesAlerts =
        activeIncidentID && incidentType === ALERTS
          ? currentIncidents[ALERTS].map((incident, incidentIdx) =>
              createMarkerCircle({
                map,
                incident,
                incidentIdx,
                incidentType: ALERTS,
              }),
            )
          : [];
      setMarkers({
        [EMERGENCY_CALLS]: mapMarkersCalls,
        [ALERTS]: mapMarkersAlerts,
      });
      setMarkerCircles({
        [EMERGENCY_CALLS]: mapMarkerCirclesCalls,
        [ALERTS]: mapMarkerCirclesAlerts,
      });
      setInfoWindows({
        [EMERGENCY_CALLS]: mapInfoWindowsCalls,
        [ALERTS]: mapInfoWindowsAlerts,
      });
    }
  };

  /**
   * Function that finds the corresponding marker and info window to be removed
   * in the all view
   * @param {String} removedID - Alert or Caller ID that maps to a marker and
   * infoWindow's rsosID
   */
  const handleMarkerRemoval = removedID => {
    let currentMarker;
    for (let i = markers[EMERGENCY_CALLS].length - 1; i >= 0; i--) {
      let marker = markers[EMERGENCY_CALLS][i];
      if (marker.rsosID === removedID) {
        currentMarker = marker;
        break;
      }
    }
    if (!currentMarker) {
      currentMarker = markers[ALERTS].find(
        marker => marker.rsosID === removedID,
      );
    }

    let currentInfoWindow;
    for (let i = infoWindows[EMERGENCY_CALLS].length - 1; i >= 0; i--) {
      let infoWindow = infoWindows[EMERGENCY_CALLS][i];
      if (
        infoWindow &&
        infoWindow?.rsosID &&
        infoWindow?.rsosID === removedID
      ) {
        currentInfoWindow = infoWindow;
        break;
      }
    }
    if (!currentInfoWindow) {
      currentInfoWindow = infoWindows[ALERTS].find(
        infoWindow => infoWindow.rsosID === removedID,
      );
    }

    clearMarker(currentMarker);
    clearInfoWindow(currentInfoWindow);
    // Check for `currentMarker` before proceeding, otherwise `incidentType`
    // could be undefined.
    if (currentMarker) {
      // Use the marker's incidentType to filter and update the remaining
      // markers.
      const { incidentType } = currentMarker;
      setMarkers({
        ...markers,
        [incidentType]: [
          ...markers[incidentType].filter(
            marker => marker.rsosID !== removedID,
          ),
        ],
      });
      setInfoWindows({
        ...infoWindows,
        [incidentType]: [
          ...infoWindows[incidentType].filter(
            infoWindow =>
              infoWindow &&
              infoWindow?.rsosID &&
              infoWindow?.rsosID !== removedID,
          ),
        ],
      });
    }
    // After removing, set removedID back to null
    onRemoveID(null);
  };

  /**
   * find the corresponding marker based on incident type and id
   *
   * @param {string} type - incident type - ALERTS or EMERGENCY_CALLS
   * @param {string} id - incident id - alert_id or caller_id
   * @returns marker object
   */
  const findMarker = (type, id) => {
    let marker;
    if (markers[type].length) {
      if (type === EMERGENCY_CALLS) {
        for (let i = markers[type].length - 1; i >= 0; i--) {
          let m = markers[type][i];
          if (m.rsosID === removedID) {
            marker = m;
            break;
          }
        }
      } else {
        marker = markers[type].find(item => {
          return item.rsosID === id;
        });
      }
    }
    return marker;
  };

  /**
   * handle w3w marker after user search for w3w
   */
  useEffect(() => {
    if (map) {
      handlew3wMarkers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasW3w, w3wCoordinates, map, isMapIconDisabled]);

  // Reset markers, circles, and info windows to the all view or to view a call
  // or alert
  useEffect(() => {
    if (isAllView || activeIncidentID) {
      clearMarkers();
      clearMarkerCircles();
      clearInfoWindows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIncidentID, isAllView]);

  // Explicitly redraw all markers when markers get reset by switching from a
  // single location to the all view (from an alert detailed view to all view,
  // a call detailed view to all view) and vice versa because
  // `currentIncidents` doesn't change when there is only a single location
  // object. See MC-17100 and MC-20780
  useEffect(() => {
    const alertLocationIsEqual = isEqual(
      previousIncidents[ALERTS],
      currentIncidents[ALERTS],
    );

    const prevMostRecentCallLocation =
      previousIncidents[EMERGENCY_CALLS]?.[
        previousIncidents[EMERGENCY_CALLS].length - 1
      ];

    const prevCallLocation = prevMostRecentCallLocation
      ? {
          latitude: prevMostRecentCallLocation?.latitude,
          longitude: prevMostRecentCallLocation?.longitude,
          uncertainty_radius: prevMostRecentCallLocation?.uncertainty_radius,
        }
      : null;

    const currentMostRecentCallLocation =
      currentIncidents[EMERGENCY_CALLS]?.[
        currentIncidents[EMERGENCY_CALLS].length - 1
      ];
    const currentCallLocation = currentMostRecentCallLocation
      ? {
          latitude: currentMostRecentCallLocation?.latitude,
          longitude: currentMostRecentCallLocation?.longitude,
          uncertainty_radius: currentMostRecentCallLocation?.uncertainty_radius,
        }
      : null;
    const shouldRedrawCall = !!(
      !markers[EMERGENCY_CALLS].length &&
      !markers[ALERTS].length &&
      currentIncidents[EMERGENCY_CALLS].length === 1 &&
      previousIncidents[EMERGENCY_CALLS].length === 1 &&
      (isAllView ||
        !!(
          activeIncidentID &&
          isEqual(
            currentIncidents[EMERGENCY_CALLS],
            previousIncidents[EMERGENCY_CALLS],
          )
        )) && // When switching from detailed view to the all calls view when
      // there is only 1 call with 1 location, markers are cleared and need to
      // be redrawn since there are no changes to the location object. When
      // switching back to the detailed view (already reverse geocoded) the
      // location object doesn't receive an update, so check if the data is the
      // same and redraw
      prevCallLocation &&
      currentCallLocation && // When switching from all calls to detailed view
      // and vice versa when there are alerts and 1 call with 1 location, do
      // not pass this condition. Changes to `currentIncidents` will trigger a
      // re-render
      !(
        (isAllView && currentIncidents[ALERTS].length) ||
        (!isAllView &&
          currentIncidents[ALERTS].length &&
          previousIncidents[ALERTS].length)
      )
    );

    const shouldRedrawAlert = !!(
      !markers[ALERTS].length &&
      !markers[EMERGENCY_CALLS].length &&
      currentIncidents[ALERTS].length === 1 &&
      previousIncidents[ALERTS].length === 1 &&
      alertLocationIsEqual &&
      !prevCallLocation &&
      !currentCallLocation &&
      ((activeIncidentID &&
        previousIncidentsData[0].alert_id ==
          currentIncidentsData[0].alert_id) ||
        isAllView)
    );

    // Neither will both be true at the same time due to `prevCallLocation` and
    // `currentCallLocation`
    if (shouldRedrawAlert || shouldRedrawCall) {
      // Trigger a redraw when there is only 1 alert and no calls. If there is
      // at least 1 call and only 1 alert, toggling between a call and the all
      // view will trigger updates to `currentIncidents` and markers will be
      // handled separately.
      drawInitialMarkers();
    }

    // Update previousIncidents and previousIncidentsData when markers get
    // updated to ensure "current" usage for this hook
    setPreviousIncidents(currentIncidents);
    setPreviousIncidentsData(currentIncidentsData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers]);

  useEffect(() => {
    Object.keys(markers).map(markerType => {
      markers[markerType].map(marker => {
        let baseOpacity;
        if (
          marker.incidentType === activeQueueTab ||
          marker?.isUnacceptedActiveAssailant ||
          marker?.isUnacceptedTrainIncident
        ) {
          baseOpacity = ACTIVE_MARKER_OPACITY;
        } else {
          baseOpacity = INACTIVE_MARKER_OPACITY;
        }

        // Apply call taker mode filtering
        const finalOpacity = getMarkerOpacity(
          marker.incidentType,
          marker.rsosID,
          baseOpacity,
          {
            activeQueueTab,
            shouldMarkerDim,
            hasActiveCallFilters,
            callQueue: filteredCalls,
            callFilters,
            workStation,
          },
        );

        marker.setOpacity(finalOpacity);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQueueTab, callFilters, hasActiveCallFilters, workStation]);

  const prevCurrentIncidentsData = usePrevious(currentIncidentsData);
  // update marker's selected state and in all views
  useEffect(() => {
    if (map) {
      if (!isAllView && activeIncidentID) {
        let selectedCall;
        for (
          let i = currentIncidents[EMERGENCY_CALLS].length - 1;
          i >= 0;
          i--
        ) {
          let incident = currentIncidents[EMERGENCY_CALLS][i];
          if (incident?.caller_id === activeIncidentID) {
            selectedCall = incident;
            break;
          }
        }
        const selectedAlert = currentIncidentsData.find(
          incident => incident?.alert_id === activeIncidentID,
        );

        if (selectedCall) {
          const marker = findMarker(EMERGENCY_CALLS, selectedCall.caller_id);
          if (marker) {
            if (
              selectedCall.source === SOURCE_SMS ||
              selectedCall.class_of_service_category === TEXT
            ) {
              marker.setIcon(SMSSelected);
            } else {
              marker.setIcon(CallSelected);
            }
          }
        }

        if (selectedAlert) {
          const marker = findMarker(ALERTS, selectedAlert.alert_id);
          const isUnacceptedActiveAssailant =
            getIsUnacceptedActiveAssailant(selectedAlert);
          const isUnacceptedTrainIncident =
            getIsUnacceptedTrainIncident(selectedAlert);
          const isTestAlert =
            selectedAlert?.emergency_type?.name?.startsWith('TEST');
          if (marker) {
            marker.setIcon(
              isUnacceptedActiveAssailant
                ? AlertActiveAssailantSelected
                : isUnacceptedTrainIncident
                  ? AlertTrainSelected
                  : isTestAlert
                    ? AlertTestSelected
                    : AlertSelected,
            );
            if (!isRegularAlert(selectedAlert) && marker) {
              clearInstanceListeners(marker, 'mouseover');
              clearInstanceListeners(marker, 'mouseout');
              handleMarkerReady(
                marker,
                map,
                selectedAlert,
                0,
                false,
                ALERTS,
                0,
              );
            }
          }
        }
      } else if (isAllView) {
        markers[ALERTS]?.map(marker => {
          const alert = currentIncidentsData.find(
            a => a.alert_id === marker.rsosID,
          );
          if (!isRegularAlert(alert)) {
            const prevAlert = prevCurrentIncidentsData.find(
              a => a.alert_id === marker.rsosID,
            );
            const alertStatus = alert?.status?.name;
            const foundIndex = currentIncidentsData.findIndex(
              data => data.alert_id === alert?.alert_id,
            );
            if (
              prevAlert?.status?.name !== ACCEPTED &&
              alertStatus === ACCEPTED
            ) {
              if (alert?.emergency_type?.name === ACTIVE_ASSAILANT) {
                marker.isUnacceptedActiveAssailant =
                  getIsUnacceptedActiveAssailant(alert);
              } else if (
                getIsTrainIncident(alert?.emergency_type?.name, true)
              ) {
                marker.isUnacceptedTrainIncident =
                  getIsUnacceptedTrainIncident(alert);
              }

              marker.setIcon(AllAlert);
              if (marker.incidentType !== activeQueueTab) {
                marker.setOpacity(INACTIVE_MARKER_OPACITY);
              }
              clearInstanceListeners(marker, 'mouseover');
              clearInstanceListeners(marker, 'mouseout');
              const events = {
                onClick: () =>
                  handleMarkerClick(alert, foundIndex, ALERTS, true),
                onMouseEnter: () =>
                  handleMouseEnter(marker, alert, foundIndex, ALERTS, 0, false),
                onMouseLeave: () =>
                  handleMouseLeave(marker, alert, foundIndex, ALERTS, 0, false),
              };

              const eventMapping = {
                onClick: 'click',
                onMouseEnter: 'mouseover',
                onMouseLeave: 'mouseout',
              };
              Object.keys(events).forEach(eventName => {
                marker.addListener(eventMapping[eventName], () => {
                  events[eventName](marker);
                });
              });
            }
          }
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    isAllView,
    activeIncidentID,
    currentIncidents,
    markers,
    currentIncidentsData,
  ]);
  // Add markers when new locations come in
  const prevMap = usePrevious(map);
  useEffect(() => {
    if (map && map !== prevMap) {
      drawInitialMarkers();
      return;
    }
    if (isAllView) {
      // CALLS
      // All calls were removed. Remove all markers, circles, and info windows
      if (
        !currentIncidents[EMERGENCY_CALLS].length &&
        markers[EMERGENCY_CALLS].length
      ) {
        clearMarkers(EMERGENCY_CALLS);
        clearMarkerCircles(EMERGENCY_CALLS);
        clearInfoWindows(EMERGENCY_CALLS);
      }

      // ALERTS
      // Currently not possible to remove all alerts at once, but it's still
      // possible to have no alerts, but have a marker on the map from removing
      // a single alert.
      if (!currentIncidents[ALERTS].length && markers[ALERTS].length) {
        clearMarkers(ALERTS);
        clearMarkerCircles(ALERTS);
        clearInfoWindows(ALERTS);
      }

      if (removedID) {
        handleMarkerRemoval(removedID);
      } else {
        // Explicitly draw all markers when switching from the detailed view to
        // the all view. Note: handling this outside of this hook results in
        // incorrect markers/circles/info windows handling
        let drawn = false;
        if (
          !numberWithNewLocation &&
          inactiveIncidentID &&
          inactiveIncidentID !== prevInactiveIncidentID &&
          !IDWithNewAlert
        ) {
          setPrevInactiveIncidentID(inactiveIncidentID);
          drawInitialMarkers();
          drawn = true;
        }
        const isSingleMarkerCase =
          (currentIncidents[EMERGENCY_CALLS].length === 1 &&
            currentIncidents[ALERTS].length === 0) ||
          (currentIncidents[ALERTS].length === 1 &&
            currentIncidents[EMERGENCY_CALLS].length === 0);
        if (
          (currentIncidents[EMERGENCY_CALLS].length ||
            currentIncidents[ALERTS].length) &&
          !markers[EMERGENCY_CALLS].length &&
          !markers[ALERTS].length &&
          !numberWithNewLocation &&
          !IDWithNewAlert &&
          !drawn &&
          !isSingleMarkerCase
        ) {
          drawInitialMarkers();
        }

        // Draw new markers for incoming locations/alerts
        if (numberWithNewLocation) {
          // If location comes in for a number that was already in the queue,
          // update the corresponding marker's location, otherwise add one.
          // Note: numberWithNewLocation will be `null` on page refresh
          const currentMarker = markers[EMERGENCY_CALLS].find(
            marker => marker.rsosID === numberWithNewLocation,
          );
          const currentInfoWindow = infoWindows[EMERGENCY_CALLS].find(
            infoWindow =>
              infoWindow &&
              infoWindow?.rsosID &&
              infoWindow?.rsosID === numberWithNewLocation,
          );
          const { mostRecentLocation, mostRecentSecondaryLocation } =
            getMostRecentLocations(filteredCalls[numberWithNewLocation]);
          const incident = mostRecentLocation || mostRecentSecondaryLocation;

          if (!currentMarker) {
            // If location comes in for a number that was not already in the
            // queue, create a marker for it. Check if the incident exists before
            // adding a marker since this condition passes on page refresh.
            // cannot add secondary location
            const isSecondaryLoc =
              incident?.class_of_service_category &&
              incident?.class_of_service_category?.includes(PHASE);
            if (incident && !isSecondaryLoc) {
              const marker = createMarker({
                currentViewType: activeQueueTab,
                map,
                incident,
                incidentIdx: 0,
                incidentType: EMERGENCY_CALLS,
                isAllView,
              });
              const infoWindow = handleMarkerReady(
                marker,
                map,
                incident,
                0,
                isAllView,
                EMERGENCY_CALLS,
              );
              setMarkers({
                ...markers,
                [EMERGENCY_CALLS]: [marker, ...markers[EMERGENCY_CALLS]],
              });
              setInfoWindows({
                ...infoWindows,
                [EMERGENCY_CALLS]: [
                  infoWindow,
                  ...infoWindows[EMERGENCY_CALLS],
                ],
              });
              handleMarkerAdded(EMERGENCY_CALLS);
            }
          } else {
            try {
              const latlng = new window.google.maps.LatLng(
                Number(incident?.latitude),
                Number(incident?.longitude),
              );
              // Check if coordinates changed before updating position since
              // this condition can pass if an alert comes in too
              if (
                parseFloat(incident?.latitude) !== parseFloat(latlng.lat) ||
                parseFloat(incident?.longitude) !== parseFloat(latlng.lng)
              ) {
                currentMarker.setPosition(latlng);
              }

              const { caller_id } = incident;
              const isTT911 = incident?.isTT911;

              if (
                isTT911 ||
                incident.source === SOURCE_SMS ||
                incident.class_of_service_category === TEXT
              ) {
                // If an agency has Agent511 text-to-911 access, and the event's source changes from
                // 'sms' to 'call', and vice versa, the event will continue to be displayed as a
                // text-to-911 request and the corresponding map marker needs to be the sms message
                // icon. The marker's isTT911 field needs to be set to true because handleMouseEnter
                // and handleMounseLeave functions do no have access to the incident's most recent
                // location data until page is refreshed.
                currentMarker.isTT911 = true;
                currentMarker.setIcon(AllCallSMS);
              } else {
                setCallStatusMarkerIcon({
                  marker: currentMarker,
                  filteredCalls,
                  hasCallStatus,
                });
              }
              const callStatus =
                hasCallStatus &&
                filteredCalls[caller_id]?.callStatuses &&
                filteredCalls[caller_id].callStatuses[
                  filteredCalls[caller_id].callStatuses.length - 1
                ];

              const callContent = (
                <InfoWindowContent
                  type={EMERGENCY_CALLS}
                  data={incident}
                  callStatus={callStatus}
                />
              );
              handleMarkerAdded(EMERGENCY_CALLS);
              // If the user is hovering over the marker, render the new content
              // into the info window, otherwise render when the info window is
              // ready
              try {
                renderIntoExistingInfoWindow(
                  `emergencycall-${caller_id}`,
                  currentInfoWindow,
                  callContent,
                );
              } catch {
                // Remove any previous 'domready' instance listeners
                clearInstanceListeners(currentInfoWindow, 'domready');
                renderIntoInfoWindow(
                  `emergencycall-${caller_id}`,
                  currentInfoWindow,
                  callContent,
                );
              }
            } catch {
              // The numberWithNewLocation incident was removed from the queue.
              // Remove the marker/infoWindow and update state.
              clearMarker(currentMarker);
              clearInfoWindow(currentInfoWindow);
              setMarkers({
                ...markers,
                [EMERGENCY_CALLS]: [
                  ...markers[EMERGENCY_CALLS].filter(
                    marker => marker.rsosID !== numberWithNewLocation,
                  ),
                ],
              });
              setInfoWindows({
                ...infoWindows,
                [EMERGENCY_CALLS]: [
                  ...infoWindows[EMERGENCY_CALLS].filter(
                    infoWindow => infoWindow.rsosID !== numberWithNewLocation,
                  ),
                ],
              });
            }
          }
        }

        if (IDWithNewAlert) {
          // Add marker for incoming alerts
          const foundIndex = currentIncidentsData.findIndex(
            data => data.alert_id === IDWithNewAlert,
          );
          const currentInfoWindow = infoWindows[ALERTS].find(
            infoWindow => infoWindow.rsosID === IDWithNewAlert,
          );
          // Check if an alert already has a marker before drawing one since
          // this condition can pass if a call comes in too
          const currentMarker = markers[ALERTS].find(
            marker => marker.rsosID === IDWithNewAlert,
          );
          const incident = currentIncidents[ALERTS][foundIndex];
          if (!currentMarker) {
            // If an alert comes in for an ID that was not already in the
            // queue, create a marker for it. Check if the incident exists before
            // adding a marker
            if (incident) {
              const marker = createMarker({
                currentViewType: activeQueueTab,
                map,
                incident,
                incidentIdx: foundIndex,
                incidentType: ALERTS,
                isAllView,
              });

              const infoWindow = handleMarkerReady(
                marker,
                map,
                incident,
                foundIndex,
                isAllView,
                ALERTS,
              );
              setMarkers({
                ...markers,
                [ALERTS]: [marker, ...markers[ALERTS]],
              });
              setInfoWindows({
                ...infoWindows,
                [ALERTS]: [infoWindow, ...infoWindows[ALERTS]],
              });
              handleMarkerAdded(ALERTS);
            }
          } else {
            try {
              const latlng = new window.google.maps.LatLng(
                Number(incident?.latitude),
                Number(incident?.longitude),
              );
              // Check if coordinates changed before updating position
              if (
                parseFloat(incident?.latitude) !== parseFloat(latlng.lat) ||
                parseFloat(incident?.longitude) !== parseFloat(latlng.lng)
              ) {
                currentMarker.setPosition(latlng);
                handleMarkerAdded(ALERTS);
              }

              try {
                const alertContent = (
                  <AlertPopup
                    data={currentIncidentsData[foundIndex]}
                    userProfile={userProfile}
                    isAllView
                  />
                );
                // can't use renderIntoExistingInfoWindow bc alert data is dynamic
                // info window doesn't exist in the DOM
                renderIntoInfoWindow(
                  IDWithNewAlert,
                  currentInfoWindow,
                  alertContent,
                );
              } catch {
                // Remove any previous 'domready' instance listeners
                clearInstanceListeners(currentInfoWindow, 'domready');
              }
            } catch {
              // The IDWithNewAlert incident was removed from the queue.
              // Remove the marker/infoWindow and update state

              clearMarker(currentMarker);
              clearInfoWindow(currentInfoWindow);
              setMarkers({
                ...markers,
                [ALERTS]: [
                  ...markers[ALERTS].filter(
                    marker => marker.rsosID !== IDWithNewAlert,
                  ),
                ],
              });
              setInfoWindows({
                ...infoWindows,
                [ALERTS]: [
                  ...infoWindows[ALERTS].filter(
                    infoWindow => infoWindow.rsosID !== IDWithNewAlert,
                  ),
                ],
              });
            }
          }
        }
      }
    } else {
      // Add marker for new locations when viewing an alert or call
      if (incidentType === ALERTS) {
        if (activeIncidentID) {
          if (markers[ALERTS].length === 0) {
            drawInitialMarkers();
          } else {
            const incident = currentIncidents[ALERTS][0];

            // TODO: PROBABLY BETTER TO DO HANDLEMOUSEENTER/HANDLEMOUSELEAVE
            // Update previous marker handling
            const oldMarker = markers[ALERTS][0];
            oldMarker.setIcon(HistoricAlertMarker);
            oldMarker.setZIndex(HISTORIC_LOC_ZINDEX);

            const newMarker = createMarker({
              currentViewType: activeQueueTab,
              map,
              incident,
              incidentIdx: 0,
              incidentType: ALERTS,
              isAllView,
              mostRecentIncidentIdx: 0,
            });

            const newInfoWindow = handleMarkerReady(
              newMarker,
              map,
              incident,
              0,
              isAllView,
              incidentType,
            );

            const oldMarkerCircle = markerCircles[ALERTS][0];
            if (oldMarkerCircle) {
              oldMarkerCircle.setOptions({
                strokeColor: 'transparent',
                fillColor: 'transparent',
                zIndex: HISTORIC_LOC_ZINDEX, // lower z index for historic locations
              });
            }

            const newMarkerCircle = createMarkerCircle({
              map,
              incident,
              incidentIdx: 0,
              incidentType: ALERTS,
            });

            setMarkers({
              ...markers,
              [ALERTS]: [newMarker, ...markers[ALERTS]],
            });
            setMarkerCircles({
              ...markerCircles,
              [ALERTS]: [newMarkerCircle, ...markerCircles[ALERTS]],
            });

            setInfoWindows({
              ...infoWindows,
              [ALERTS]: [newInfoWindow, ...infoWindows[ALERTS]],
            });
            handleMarkerAdded(ALERTS);
          }
        }
      } else {
        if (activeIncidentID) {
          // If there are no markers, draw them for all locations, otherwise
          // add one for new location. This occurs when clicking on the queue
          // or on page refresh
          if (markers[EMERGENCY_CALLS].length === 0) {
            // Do not draw here if there are alerts and 1 call and the call has
            // only 1 location.The redraw will be triggered by the hook
            // watching for [markers]. See MC-24372. Related to MC-20780.
            if (
              !currentIncidents[EMERGENCY_CALLS].length === 1 ||
              !isEqual(
                currentIncidents[EMERGENCY_CALLS],
                previousIncidents[EMERGENCY_CALLS],
              )
            ) {
              drawInitialMarkers();
            }
          } else {
            // Add marker for new location

            let incident;
            for (
              let i = currentIncidents[EMERGENCY_CALLS].length - 1;
              i >= 0;
              i--
            ) {
              let inc = currentIncidents[EMERGENCY_CALLS][i];
              if (inc?.caller_id === activeIncidentID) {
                incident = inc;
                break;
              }
            }

            // Note: For some reason this gets triggered multiple times AFTER
            // it has already triggered once.
            // Added check for incidents.length > markers.length to prevent
            if (
              incident &&
              currentIncidents[EMERGENCY_CALLS].length >
                markers[EMERGENCY_CALLS].length
            ) {
              // Note: need to clear all here because the listener added during first time creating it, however, the index change
              // from the original code below so the listener stayed with index = 0 resulting showing all green markers after
              // hover. Custom map component is needed based on the design trend for new features.
              clearMarkers(EMERGENCY_CALLS);
              clearMarkerCircles(EMERGENCY_CALLS);
              clearInfoWindows(EMERGENCY_CALLS);
              const mostRecentCallsIdx =
                currentIncidents[EMERGENCY_CALLS].length - 1;
              const mapMarkersCalls = currentIncidents[EMERGENCY_CALLS].map(
                (incident, incidentIdx) => {
                  return createMarker({
                    currentViewType: activeQueueTab,
                    map,
                    incident,
                    incidentIdx,
                    incidentType: EMERGENCY_CALLS,
                    isAllView,
                    mostRecentIncidentIdx: mostRecentCallsIdx,
                  });
                },
              );
              const mapInfoWindowsCalls = mapMarkersCalls.map(
                (marker, markerIdx) =>
                  handleMarkerReady(
                    marker,
                    map,
                    currentIncidents[EMERGENCY_CALLS][markerIdx],
                    markerIdx,
                    isAllView,
                    EMERGENCY_CALLS,
                    mostRecentCallsIdx,
                  ),
              );
              const mapMarkerCirclesCalls = currentIncidents[
                EMERGENCY_CALLS
              ].map((incident, incidentIdx) =>
                createMarkerCircle({
                  map,
                  incident,
                  incidentIdx,
                  incidentType: EMERGENCY_CALLS,
                  mostRecentIncidentIdx: mostRecentCallsIdx,
                }),
              );

              // If markers exist, update the previous marker's icon
              if (markers[EMERGENCY_CALLS].length) {
                const mostRecentCallMarker =
                  markers[EMERGENCY_CALLS][markers[EMERGENCY_CALLS].length - 1];
                mostRecentCallMarker.setIcon(HistoricCallMarker);
                mostRecentCallMarker.setZIndex(HISTORIC_LOC_ZINDEX); // lower z index for historic locations
              }

              // If circles exist, update the previous circle and add
              // event listeners for showing uncertainty radius
              if (markerCircles[EMERGENCY_CALLS].length) {
                const oldMarkerCircle =
                  markerCircles[EMERGENCY_CALLS][
                    markerCircles[EMERGENCY_CALLS].length - 1
                  ];
                if (oldMarkerCircle) {
                  oldMarkerCircle.setOptions({
                    strokeColor: 'transparent',
                    fillColor: 'transparent',
                    zIndex: HISTORIC_LOC_ZINDEX, // lower z index for historic locations
                  });
                  oldMarkerCircle.addListener('mouseover', () => {
                    oldMarkerCircle.setOptions({
                      strokeColor: '#06c08b',
                      fillColor: '#06c08b',
                    });
                  });
                  oldMarkerCircle.addListener('mouseout', () => {
                    oldMarkerCircle.setOptions({
                      strokeColor: 'transparent',
                      fillColor: 'transparent',
                    });
                  });
                }
              }

              setMarkers({
                ...markers,
                [EMERGENCY_CALLS]: mapMarkersCalls,
              });
              setMarkerCircles({
                ...markers,
                [EMERGENCY_CALLS]: mapMarkerCirclesCalls,
              });
              setInfoWindows({
                ...infoWindows,
                [EMERGENCY_CALLS]: mapInfoWindowsCalls,
              });
              handleMarkerAdded(EMERGENCY_CALLS);
            }
          }
        } else {
          // Cleared all data in QV. Remove markers
          clearMarkers(EMERGENCY_CALLS);
          clearMarkerCircles(EMERGENCY_CALLS);
          clearInfoWindows(EMERGENCY_CALLS);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIncidents, map]);

  // Update markers' icon when new secondary locations come in
  useEffect(() => {
    if (isAllView && numberWithNewLocation) {
      const currentMarker = markers[EMERGENCY_CALLS].find(
        marker => marker.rsosID === numberWithNewLocation,
      );
      const call = filteredCalls[numberWithNewLocation];
      const mostRecentCall = getMostRecentLoc(call);
      const isMostRecentSecondary =
        mostRecentCall?.class_of_service_category?.includes(PHASE);
      if (currentMarker && isMostRecentSecondary) {
        setCallStatusMarkerIcon({
          marker: currentMarker,
          filteredCalls,
          hasCallStatus,
          hoveredID,
        });
      }
      if (isMostRecentSecondary) {
        handleMarkerAdded(EMERGENCY_CALLS);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberWithNewLocation]);

  useEffect(() => {
    // Reset zoom level to `null` so when viewing an incident or switching
    // between incidents, the zoom level defaults to 16 until zoom level is
    // changed by user
    if (map && !isAllView) {
      setZoomLevel(null);
    }
  }, [isAllView, map]);

  useEffect(() => {
    // If markers/circles update when viewing activeIncidentID, update the bounds
    if (markers[incidentType].length && !isAllView) {
      // Handle zoom and position after marker is drawn
      // might need to listen to the reversed length - 1 of markers[incidentsType]

      let position = markers[incidentType][0].position;
      if (incidentType === EMERGENCY_CALLS) {
        const mostRecentCallsIdx = markers[EMERGENCY_CALLS].length - 1;
        position = markers[EMERGENCY_CALLS][mostRecentCallsIdx].position;
      }
      const currentZoomLevel = map.getZoom();

      // If the marker isn't on the map, don't zoom to it
      if (markers[incidentType]?.[0]?.map === null) {
        centerMapOnJursidiction(boundingBox, map);
        return;
      }

      const zoom =
        map.getZoom() < DEFAULT_ZOOM_LEVEL ? DEFAULT_ZOOM_LEVEL : null;

      // If it's an emergency call, let CallsGL.js
      // handle panning. The markers array doesn't know about secondary
      // locations, and CallsGL.js handles panning to the most recent
      // location whether primary or secondary.
      if (incidentType !== EMERGENCY_CALLS) {
        panMapTo({
          mapProvider: GOOGLE,
          mapInstance: map,
          latitude: position.lat(),
          longitude: position.lng(),
          zoom,
        });
      }
      // Set zoom level on initial drawing of markers
      if (!zoomLevel) {
        setZoomLevel(16);
      } else {
        setZoomLevel(currentZoomLevel);
      }
      if (markerCircles[incidentType].length) {
        const mostRecentIncidentIdx = markers[incidentType].length - 1;
        markers[incidentType].forEach((marker, idx) => {
          const isHistoricLoc =
            (incidentType === ALERTS && idx > 0) ||
            (incidentType === EMERGENCY_CALLS && idx < mostRecentIncidentIdx);

          if (isHistoricLoc) {
            clearInstanceListeners(marker, 'mouseover');
            clearInstanceListeners(marker, 'mouseout');
            marker.addListener(
              'mouseover',
              () => {
                if (incidentType === ALERTS) {
                  marker.setIcon(HistoricAlertMarkerHover);
                } else {
                  marker.setIcon(HistoricCallMarkerHover);
                }

                markerCircles[incidentType][idx].setOptions({
                  strokeColor: incidentType === ALERTS ? '#5C6E9D' : '#06c08b',
                  fillColor: incidentType === ALERTS ? '#5C6E9D' : '#06c08b',
                });
              },
              true,
            );
            marker.addListener(
              'mouseout',
              () => {
                if (incidentType === ALERTS) {
                  marker.setIcon(HistoricAlertMarker);
                } else {
                  marker.setIcon(HistoricCallMarker);
                }
                markerCircles[incidentType][idx].setOptions({
                  strokeColor: 'transparent',
                  fillColor: 'transparent',
                });
              },
              true,
            );
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, markerCircles]);

  // Show marker's corresponding info window and update its icon
  useEffect(() => {
    if (hoveredID) {
      // Find the corresponding infoWindow

      let currentInfoWindow;
      for (let i = infoWindows[EMERGENCY_CALLS].length - 1; i >= 0; i--) {
        let infoWindow = infoWindows[EMERGENCY_CALLS][i];
        if (infoWindow && infoWindow.rsosID === hoveredID) {
          currentInfoWindow = infoWindow;
          break;
        } else {
          infoWindow?.close();
        }
      }
      if (!currentInfoWindow) {
        currentInfoWindow = infoWindows[ALERTS].find(
          infoWindow => infoWindow.rsosID === hoveredID,
        );
      }
      // Find the corresponding marker

      let currentMarker;

      for (let i = markers[EMERGENCY_CALLS].length - 1; i >= 0; i--) {
        let marker = markers[EMERGENCY_CALLS][i];
        if (marker.rsosID === hoveredID) {
          currentMarker = marker;
          break;
        }
      }
      if (!currentMarker) {
        currentMarker = markers[ALERTS].find(m => m.rsosID === hoveredID);
      }
      if (currentInfoWindow) {
        setActiveInfoWindow(currentInfoWindow);
        // Hide all other info windows
        Object.keys(infoWindows).forEach(infoWindowType => {
          infoWindows[infoWindowType].forEach(infoWindow => {
            if (infoWindow?.rsosID !== hoveredID) {
              infoWindow.close();
            }
          });
        });
      }
      // Update marker icon
      if (currentMarker) {
        const currentMarkerType = currentMarker.incidentType;
        if (currentMarkerType === ALERTS) {
          const currentIncident = currentIncidentsData.find(
            incident => incident?.alert_id === hoveredID,
          );
          const isUnacceptedActiveAssailant =
            getIsUnacceptedActiveAssailant(currentIncident);
          const isUnacceptedTrainIncident =
            getIsUnacceptedTrainIncident(currentIncident);
          const isTestAlert =
            currentIncident?.emergency_type?.name?.startsWith('TEST');
          if (isAllView) {
            currentIncident?.isUnread
              ? currentMarker.setIcon(AllAlertUnreadHover)
              : currentMarker.setIcon(
                  isUnacceptedActiveAssailant
                    ? AllAlertActiveAssailantHover
                    : isUnacceptedTrainIncident
                      ? AllAlertTrainHover
                      : isTestAlert
                        ? AllAlertTestHover
                        : AllAlertHover,
                );
          } else {
            currentMarker.setIcon(
              isUnacceptedActiveAssailant
                ? AlertActiveAssailantSelected
                : isUnacceptedTrainIncident
                  ? AlertTrainSelected
                  : isTestAlert
                    ? AlertTestSelected
                    : AlertSelected,
            );
          }
        } else {
          let currentIncident;
          for (
            let i = currentIncidents[EMERGENCY_CALLS].length - 1;
            i >= 0;
            i--
          ) {
            let incident = currentIncidents[EMERGENCY_CALLS][i];
            if (incident?.caller_id === hoveredID) {
              currentIncident = incident;
              break;
            }
          }

          // If currentIncident doesn't exist, all calls were removed. The
          // markers haven't been removed before a hover took place
          if (currentIncident) {
            const isSMS = !!(
              currentIncident &&
              (currentIncident?.source === SOURCE_SMS ||
                currentIncident?.isTT911 ||
                currentIncident?.class_of_service_category === TEXT)
            );

            if (isSMS) {
              if (isAllView) {
                currentMarker.setIcon(AllCallSMSHover);
              } else {
                currentMarker.setIcon(SMSSelected);
              }
            } else {
              if (isAllView) {
                setCallStatusMarkerIcon({
                  marker: currentMarker,
                  filteredCalls,
                  hoveredID,
                  hasCallStatus,
                });
              } else {
                setAniAliMarkerIcon(currentMarker, currentIncident);
              }
            }
          }
        }

        // Revert icons for all other markers
        Object.keys(markers).forEach(markerType => {
          markers[markerType].forEach(marker => {
            if (marker.rsosID && marker.rsosID !== hoveredID) {
              revertMarkerIcon(marker, hasCallStatus);
            }
          });
        });
        setActiveMarker(currentMarker);
      }
      if (currentInfoWindow?.shouldShowWindow && currentMarker) {
        currentInfoWindow.open(map, currentMarker);
      }
    } else {
      if (activeInfoWindow) {
        activeInfoWindow.close();
      }
      // Revert marker icon
      if (activeMarker) {
        if (
          activeMarker?.rsosID &&
          activeMarker?.incidentType === EMERGENCY_CALLS &&
          isAllView
        ) {
          let prevIncident;
          for (
            let i = currentIncidents[EMERGENCY_CALLS].length - 1;
            i >= 0;
            i--
          ) {
            let incident = currentIncidents[EMERGENCY_CALLS][i];
            if (incident?.caller_id === activeMarker?.rsosID) {
              prevIncident = incident;
              break;
            }
          }
          if (
            prevIncident?.source === SOURCE_SMS ||
            activeMarker.isTT911 ||
            prevIncident?.class_of_service_category === TEXT
          ) {
            activeMarker.setIcon(AllCallSMS);
          } else {
            setCallStatusMarkerIcon({
              marker: activeMarker,
              filteredCalls,
              hoveredID,
              hasCallStatus,
            });
          }
        } else {
          revertMarkerIcon(activeMarker, hasCallStatus);
        }
      }
      setActiveInfoWindow(null);
      setActiveMarker(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredID]);

  // update markers when there are unread alerts
  useEffect(() => {
    if (map && isAllView && markers[ALERTS].length && unreadAlerts.length) {
      unreadAlerts.forEach(alert => {
        const alertID = alert.alert_id;
        const marker = findMarker(ALERTS, alertID);
        if (marker) {
          marker.isUnread = alert.isUnread;
          alert.isUnread
            ? marker.setIcon(AllAlertUnread)
            : marker.setIcon(AllAlert);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isAllView, markers, unreadAlerts]);

  // sync the alert object changing in the queue with the info window on hover
  const prevHoveredID = usePrevious(hoveredID);
  useEffect(() => {
    if (isAllView && hoveredID) {
      const alertHoveredCurrent = currentIncidentsData.find(
        a => a.alert_id === hoveredID,
      );
      if (alertHoveredCurrent) {
        const currentInfoWindow = infoWindows[ALERTS].find(
          infoWindow => infoWindow.rsosID === hoveredID,
        );
        const alertContent = (
          <AlertPopup
            data={alertHoveredCurrent}
            userProfile={userProfile}
            isAllView
          />
        );
        if (prevHoveredID === hoveredID) {
          //case when the user keeps hovering on a queue item while the status change
          try {
            renderIntoExistingInfoWindow(
              hoveredID,
              currentInfoWindow,
              alertContent,
            );
          } catch {
            clearInstanceListeners(currentInfoWindow, 'domready');
            renderIntoInfoWindow(hoveredID, currentInfoWindow, alertContent);
          }
        } else {
          clearInstanceListeners(currentInfoWindow, 'domready');
          renderIntoInfoWindow(hoveredID, currentInfoWindow, alertContent);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredID, isAllView, currentIncidentsData]);

  useEffect(() => {
    if (declinedAlertID) {
      handleMarkerRemoval(declinedAlertID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declinedAlertID]);

  useEffect(() => {
    if (hasEagleViewAccess) {
      if (
        isMapIconDisabled &&
        (markers?.calls?.length || markers?.alerts?.length)
      ) {
        markers.calls.forEach(marker => {
          clearInstanceListeners(marker);
          marker.addListener('click', handleObliqueImageryMarkerClicked);
          marker.setCursor('crosshair');
        });
        markers.alerts.forEach(marker => {
          clearInstanceListeners(marker);
          marker.addListener('click', handleObliqueImageryMarkerClicked);
          marker.setCursor('crosshair');
        });
      } else if (
        !isMapIconDisabled &&
        (markers?.calls?.length || markers?.alerts?.length)
      ) {
        // readd infowindow
        if (isAllView) {
          // all calls
          const mapInfoWindowsCalls = markers.calls.map((marker, idx) => {
            clearInstanceListeners(marker);
            marker.setCursor('pointer');

            const {
              incident,
              incidentType,
              mostRecentIncidentIdx = 0,
            } = marker;
            return handleMarkerReady(
              marker,
              map,
              incident,
              idx,
              isAllView,
              incidentType,
              mostRecentIncidentIdx,
            );
          });
          // all alerts
          const isAlertsEqual =
            markers.alerts.length === currentIncidentsData.length;

          let normalizeAlertsMarkers = markers.alerts;
          if (!isAlertsEqual) {
            normalizeAlertsMarkers = normalizeAlertsMarkers
              .map(marker => {
                if (
                  !currentIncidentsData.some(
                    incidentData => incidentData.alert_id === marker.rsosID,
                  )
                ) {
                  handleMarkerRemoval(marker.rsosID);
                  return null;
                }
                return marker;
              })
              .filter(marker => marker);
          }
          const mapInfoWindowsAlerts = normalizeAlertsMarkers.map(
            (marker, idx) => {
              clearInstanceListeners(marker);
              marker.setCursor('pointer');
              const {
                incident,
                incidentType,
                mostRecentIncidentIdx = 0,
              } = marker;
              return handleMarkerReady(
                marker,
                map,
                incident,
                idx,
                isAllView,
                incidentType,
                mostRecentIncidentIdx,
              );
            },
          );
          setInfoWindows({
            [EMERGENCY_CALLS]: mapInfoWindowsCalls,
            [ALERTS]: mapInfoWindowsAlerts,
          });
        } else {
          const mostRecentCallMarker = markers.calls[markers.calls.length - 1];
          const mostRecentAlertMarker = markers.alerts[0];
          let mapInfoWindowsCall = [];
          let mapInfoWindowsAlert = [];
          // detailed call
          if (mostRecentCallMarker) {
            clearInstanceListeners(mostRecentCallMarker);

            mostRecentCallMarker.setCursor('pointers');
            const {
              incident,
              incidentIdx,
              incidentType,
              mostRecentIncidentIdx = 0,
            } = mostRecentCallMarker;
            mapInfoWindowsCall.push(
              handleMarkerReady(
                mostRecentCallMarker,
                map,
                incident,
                incidentIdx,
                isAllView,
                incidentType,
                mostRecentIncidentIdx,
              ),
            );
          }
          // detailed alert
          if (mostRecentAlertMarker) {
            clearInstanceListeners(mostRecentAlertMarker);
            mostRecentAlertMarker.setCursor('pointer');
            const {
              incident,
              incidentIdx,
              incidentType,
              mostRecentIncidentIdx = 0,
            } = mostRecentAlertMarker;
            mapInfoWindowsAlert.push(
              handleMarkerReady(
                mostRecentAlertMarker,
                map,
                incident,
                incidentIdx,
                isAllView,
                incidentType,
                mostRecentIncidentIdx,
              ),
            );
          }
          setInfoWindows({
            [EMERGENCY_CALLS]: mapInfoWindowsCall,
            [ALERTS]: mapInfoWindowsAlert,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapIconDisabled, markers, hasEagleViewAccess, currentIncidentsData]);

  return null;
};

Markers.propTypes = {
  activeIncidentID: PropTypes.string,
  currentIncidents: PropTypes.shape({
    [EMERGENCY_CALLS]: PropTypes.array,
    [ALERTS]: PropTypes.array,
  }),
  currentIncidentsData: PropTypes.array,
  IDWithNewAlert: PropTypes.string,
  // Only EMERGENCY_CALLS and ALERTS produce map markers. Other values
  // (e.g. AGENCY_CHAT) are passed through when those tabs are active
  // but are safely ignored — no markers are rendered.
  incidentType: PropTypes.string,
  isAllView: PropTypes.bool,
  map: PropTypes.object,
  numberWithNewLocation: PropTypes.string,
  onMarkerClick: PropTypes.func,
  onMarkerHover: PropTypes.func,
  onRemoveID: PropTypes.func,
  removedID: PropTypes.string,
  userProfile: PropTypes.object,
};

export default Markers;
